import { getUuid } from '@sibe/utilities/common';
import type { HsGeometry } from '../../graphic-engine-new/geometry/geometry.interface';
import type { HsRegularGeometry } from '../../graphic-engine-new/geometry/regular-geometry.class';
import { HsIndexedGeometry } from '../../graphic-engine-new/geometry/indexed-geometry.class';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import { HsGraphicCollection } from '../../graphic-render-new/objects/graphic-collection.class';
import { HsGraphicLines } from '../../graphic-render-new/objects/graphic-lines.class';
import { HsGraphicPoints } from '../../graphic-render-new/objects/graphic-points.class';
import type { HsPointsMaterial } from '../../graphic-render-new/materials/points-material.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import { hsMakeGeometry } from '../../tools-new/geometry-utils/make-geometry.function';
import { hsMakePointCloudGeometry } from '../../tools-new/geometry-utils/make-point-cloud-geometry.function';
import { HsRegularGeometryData } from '../../tools-new/geometry-utils/regular-geometry-data.class';
import type { HsDecodedLine, HsDecodedMesh, HsDecodedPointCloud } from '../data/decoded-geometry.interface';
import { hsScenePointCloudColors, type HsScenePointCloudColors } from './scene-point-cloud-colors.function';
import {
  hsBuildSceneMeshGeometry,
  type HsSceneMeshGeometryResult,
  type HsSceneMeshRejection,
} from './scene-mesh-geometry.function';
import { HsTexturedIndexedGeometry } from '../../graphic-engine-new/geometry/textured-indexed-geometry.class';
import { HsTextureMaterial } from '../../graphic-render-new/materials/texture-material.class';
import { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';

/** What the scene has to update when its objects change; each hook is cheap and may run often. */
export interface HsSceneObjectsHooks {
  /** Scene geometry totals (triangles, buffers) are stale. */
  invalidateTotals(): void;
  /** Object bounds or membership changed; spatial culling must refresh. */
  refreshCulling(): void;
  /** The set of drawn objects changed; the draw order must be rebuilt. */
  invalidateOrder(): void;
  /** Recomputes a reused object's bounds after its matrix changed (default: its bounding sphere only). */
  updateBounds?(object: HsGraphicObject): void;
}

/** Where a mesh's object came from in `upsertMesh`. */
export type HsSceneMeshUpsertKind = 'reused' | 'collection' | 'shared' | 'created';

/** The outcome of `upsertMesh`; without an object, `rejection` says why the mesh was not drawn. */
export interface HsSceneMeshUpsertResult {
  object: HsGraphicObject | null;
  kind: HsSceneMeshUpsertKind | null;
  rejection: HsSceneMeshRejection | null;
  /** The indexed build failed and a fallback was tried. */
  indexedBuildFailed: boolean;
}

export interface HsSceneMeshUpsertOptions {
  /** Geometry id the mesh's instances share (the render key's geometry). */
  sourceGeometryId: string;
  /** Matrix of the object; material parts use their parent's. */
  worldMatrix: readonly number[] | undefined;
  /** Matrix of the render key itself, used when the mesh reuses another instance's geometry. */
  instanceWorldMatrix: readonly number[] | undefined;
  /** Share geometry between instances of one source mesh; off for material parts. */
  shareSourceGeometry: boolean;
  /** Build indexed geometry ready for instanced drawing. */
  instancingGeometry: boolean;
}

/** A drawn object and whether it was kept from an earlier frame. */
export interface HsSceneUpsertResult<T extends HsGraphicObject> {
  object: T;
  reused: boolean;
}

/** Triangles of the scene's visible geometry and the instanced batches it holds. */
export interface HsSceneGeometryTotals {
  triangles: number;
  totalInstancedBatches: number;
}

/** Scene geometry totals plus what the current visible lists draw. */
export interface HsSceneRenderTotals extends HsSceneGeometryTotals {
  drawCalls: number;
  /** Instanced batches the camera does not see. */
  culledInstancedBatches: number;
  instancedDrawCalls: number;
  instancedInstances: number;
}

/** Visible objects of a frame, as a culling pass leaves them. */
export interface HsSceneVisibleLists {
  unsortedObjects: HsGraphicObject[];
  sortedObjects: HsGraphicObject[];
}

/** Draw calls of a list of objects. */
export interface HsSceneDrawCalls {
  drawCalls: number;
  instancedDrawCalls: number;
  instancedInstances: number;
}

/** A renderer that merges mesh draws; `HsRender` estimates its real draw calls. */
export interface HsSceneDrawBatching {
  meshDrawBatchingEnabled: boolean;
  estimateMeshDrawCalls(objects: HsGraphicObject[], transparent: boolean): HsSceneDrawCalls;
}

/** The queue side of removal; `HsGraphicQueue` provides it. */
export interface HsSceneObjectQueue {
  removeObject(object: HsGraphicObject): void;
  addObject(object: HsGraphicObject): void;
}

/**
 * The GPU objects of a BIM scene by render key. Several render keys can share one object (instanced
 * batches) and several objects can share one geometry (instancing by source geometry), so removal counts
 * geometry references and deletes a buffer only when its last user goes.
 */
export class HsSceneObjects {
  public meshes: Map<string, HsGraphicObject> = new Map();
  /** First drawn object of each source geometry; instances copy its geometry. */
  public meshSources: Map<string, HsGraphicObject> = new Map();
  public lines: Map<string, HsGraphicLines> = new Map();
  public pointClouds: Map<string, HsGraphicPoints> = new Map();
  /** Render keys of each instanced batch in instance order, for picking a single instance. */
  public renderKeysByCollection: WeakMap<HsGraphicCollection, readonly string[]> = new WeakMap();

  /** Objects `applyHidden` took out of the queue; only these come back when their keys are shown again. */
  private readonly hiddenObjects: Set<HsGraphicObject> = new Set();
  /** Batches with instances `applyHidden` hid. */
  private readonly partlyHiddenCollections: Set<HsGraphicCollection> = new Set();
  private totalsRevision: number = 0;
  private cachedTotals: HsSceneGeometryTotals | null = null;
  private cachedTotalsRevision: number = -1;

  public constructor(private readonly hooks: HsSceneObjectsHooks) {}

  /** Marks the geometry totals stale; objects call it themselves, hosts call it after outside changes. */
  public invalidateTotals(): void {
    this.totalsRevision++;
  }

  /** The geometry totals, recounted only after `invalidateTotals`. */
  public geometryTotals(): HsSceneGeometryTotals {
    if (this.cachedTotals === null || this.cachedTotalsRevision !== this.totalsRevision) {
      this.cachedTotals = this.measureGeometryTotals();
      this.cachedTotalsRevision = this.totalsRevision;
    }
    return this.cachedTotals;
  }

  /**
   * Counts the triangles of every visible tracked object (an object shared by several render keys once,
   * instanced batches times their instances) and the instanced batches, visible or not.
   */
  public measureGeometryTotals(): HsSceneGeometryTotals {
    const counted: Set<HsGraphicObject> = new Set(this.meshes.values());
    let triangles: number = 0;
    let totalInstancedBatches: number = 0;
    const groups: Iterable<HsGraphicObject>[] = [
      counted,
      this.meshSources.values(),
      this.lines.values(),
      this.pointClouds.values(),
    ];
    for (const objects of groups) {
      const isMeshGroup: boolean = objects === counted;
      for (const object of objects) {
        if (!isMeshGroup) {
          if (counted.has(object)) {
            continue;
          }
          counted.add(object);
        }
        if (object.isDeleted || object.geometry.isDeleted) {
          continue;
        }
        const isCollection: boolean = object instanceof HsGraphicCollection;
        if (isMeshGroup && isCollection) {
          totalInstancedBatches++;
        }
        if (!object.visible || object instanceof HsGraphicLines || object instanceof HsGraphicPoints) {
          continue;
        }
        const instances: number = isCollection ? (object as HsGraphicCollection).getCount() : 1;
        triangles += hsCountGeometryTriangles(object.geometry) * instances;
      }
    }
    return { triangles, totalInstancedBatches };
  }

  /**
   * The geometry totals and what `visible` draws: draw calls, instanced draws and instances, and the
   * instanced batches it leaves out. With mesh draw batching on, `batching` estimates the merged calls.
   */
  public renderTotals(visible: HsSceneVisibleLists | null, batching?: HsSceneDrawBatching): HsSceneRenderTotals {
    let drawCalls: number = 0;
    let instancedDrawCalls: number = 0;
    let instancedInstances: number = 0;
    if (visible !== null) {
      for (const objects of [visible.unsortedObjects, visible.sortedObjects]) {
        for (const object of objects) {
          if (!object.visible || object.isDeleted || object.geometry.isDeleted) {
            continue;
          }
          drawCalls++;
          if (object instanceof HsGraphicCollection) {
            instancedDrawCalls++;
            instancedInstances += object.getCount();
          }
        }
      }
    }
    const geometry: HsSceneGeometryTotals = this.geometryTotals();
    const culledInstancedBatches: number = Math.max(0, geometry.totalInstancedBatches - instancedDrawCalls);
    if (batching?.meshDrawBatchingEnabled === true && visible !== null) {
      const opaque: HsSceneDrawCalls = batching.estimateMeshDrawCalls(visible.unsortedObjects, false);
      const transparent: HsSceneDrawCalls = batching.estimateMeshDrawCalls(visible.sortedObjects, true);
      drawCalls = opaque.drawCalls + transparent.drawCalls;
      instancedDrawCalls = opaque.instancedDrawCalls + transparent.instancedDrawCalls;
      instancedInstances = opaque.instancedInstances + transparent.instancedInstances;
    }
    return {
      triangles: geometry.triangles,
      totalInstancedBatches: geometry.totalInstancedBatches,
      drawCalls,
      culledInstancedBatches,
      instancedDrawCalls,
      instancedInstances,
    };
  }

  /** References to each live geometry from the tracked objects, for batch removals. */
  public countGeometryReferences(): Map<HsGeometry, number> {
    const counts: Map<HsGeometry, number> = new Map<HsGeometry, number>();
    for (const object of this.trackedObjects()) {
      if (object.isDeleted || object.geometry.isDeleted) {
        continue;
      }
      this.retainGeometryReference(object, counts);
    }
    return counts;
  }

  /**
   * Removes the object from the queue and frees it. Its geometry buffer is deleted only when no other
   * object uses it, judged by `referenceCounts` when given and by the tracked objects otherwise.
   */
  public dispose(
    queue: HsSceneObjectQueue,
    graphics: HsGraphics,
    object: HsGraphicObject,
    referenceCounts?: Map<HsGeometry, number>
  ): void {
    this.hooks.invalidateOrder();
    this.hooks.refreshCulling();
    queue.removeObject(object);
    // Several render keys can share one batch, but it owns only one geometry reference.
    if (object.isDeleted) {
      return;
    }
    this.invalidateTotals();
    this.hooks.invalidateTotals();
    this.hiddenObjects.delete(object);
    if (object instanceof HsGraphicCollection) {
      this.partlyHiddenCollections.delete(object);
      object.clear(graphics);
    }
    const stillReferenced: boolean =
      referenceCounts === undefined
        ? this.isGeometryUsedByAnotherObject(object)
        : this.releaseGeometryReference(object, referenceCounts) > 0;
    if (!object.geometry.isDeleted && !stillReferenced) {
      graphics.deleteBuffer(object.geometry.buffer);
      object.geometry.isDeleted = true;
    }
    object.isDeleted = true;
  }

  /** Whether `applyHidden` hides anything now; a host can skip the call when this is false and nothing is hidden. */
  public get hasHidden(): boolean {
    return this.hiddenObjects.size > 0 || this.partlyHiddenCollections.size > 0;
  }

  /**
   * Hides the tracked render keys `isHidden` accepts and shows the others again, keeping every object and its
   * geometry: an object leaves the queue when all its keys are hidden, an instanced batch hides single
   * instances in its instance buffer. Objects out of the queue for other reasons stay out. Cost is one pass
   * over the tracked keys, so hosts can call it after every change of the drawn objects. Returns whether
   * anything changed.
   */
  public applyHidden(
    isHidden: (renderKey: string) => boolean,
    queue: HsSceneObjectQueue & { objects: ReadonlyMap<string, unknown> },
    graphics: HsGraphics
  ): boolean {
    const keysByObject: Map<HsGraphicObject, string[]> = new Map();
    for (const map of [this.meshes, this.lines, this.pointClouds] as Map<string, HsGraphicObject>[]) {
      for (const [renderKey, object] of map) {
        if (object.isDeleted) {
          continue;
        }
        const keys: string[] | undefined = keysByObject.get(object);
        if (keys === undefined) {
          keysByObject.set(object, [renderKey]);
        } else {
          keys.push(renderKey);
        }
      }
    }
    let changed: boolean = false;
    for (const object of this.hiddenObjects) {
      // Dropped while hidden: whoever dropped it decides whether it comes back.
      if (!keysByObject.has(object)) {
        this.hiddenObjects.delete(object);
      }
    }
    for (const collection of this.partlyHiddenCollections) {
      if (!keysByObject.has(collection)) {
        this.partlyHiddenCollections.delete(collection);
      }
    }
    for (const [object, keys] of keysByObject) {
      let hidden: boolean;
      if (object instanceof HsGraphicCollection) {
        // A visibility prune drops keys but leaves the batch as built (refreshes that only change visibility do not
        // rebuild batches), so instances whose key no longer points at this batch are masked like hidden ones.
        const instanceKeys: readonly string[] = this.renderKeysByCollection.get(object) ?? keys;
        const hiddenInstances: Set<number> = new Set();
        instanceKeys.forEach((renderKey: string, index: number) => {
          if (isHidden(renderKey) || this.meshes.get(renderKey) !== object) {
            hiddenInstances.add(index);
          }
        });
        changed = object.setHiddenInstances(hiddenInstances, graphics) || changed;
        if (hiddenInstances.size > 0) {
          this.partlyHiddenCollections.add(object);
        } else {
          this.partlyHiddenCollections.delete(object);
        }
        hidden = object.getCount() > 0 && object.getVisibleCount() === 0;
      } else {
        hidden = keys.every(isHidden);
      }
      if (hidden) {
        if (queue.objects.has(object.uuid)) {
          queue.removeObject(object);
          this.hiddenObjects.add(object);
          changed = true;
        }
      } else if (this.hiddenObjects.delete(object)) {
        if (!queue.objects.has(object.uuid)) {
          queue.addObject(object);
        }
        changed = true;
      }
    }
    if (changed) {
      this.invalidateTotals();
      this.hooks.invalidateTotals();
      this.hooks.refreshCulling();
      this.hooks.invalidateOrder();
    }
    return changed;
  }

  /**
   * Drops tracked entries whose render key is not in `activeIds`. An object still used by an active key
   * stays. With `preserveGeometry` (visibility changes), removed objects only leave the queue and keep
   * their geometry for when they come back.
   */
  public prune<T extends HsGraphicObject>(
    activeIds: ReadonlySet<string>,
    tracked: Map<string, T>,
    queue: HsSceneObjectQueue,
    graphics: HsGraphics,
    preserveGeometry: boolean = false,
    referenceCounts?: Map<HsGeometry, number>
  ): void {
    const activeObjects: Set<T> = new Set<T>();
    for (const [renderKey, object] of tracked.entries()) {
      if (activeIds.has(renderKey)) {
        activeObjects.add(object);
      }
    }
    for (const [renderKey, object] of tracked.entries()) {
      if (activeIds.has(renderKey)) {
        continue;
      }
      this.invalidateTotals();
      this.hooks.invalidateTotals();
      this.hooks.refreshCulling();
      tracked.delete(renderKey);
      if (activeObjects.has(object)) {
        continue;
      }
      if (preserveGeometry) {
        this.hooks.invalidateOrder();
        queue.removeObject(object);
        continue;
      }
      this.dispose(queue, graphics, object, referenceCounts);
    }
  }

  /**
   * Draws the meshes of `renderKeys` (same indexed geometry and look) as one instanced batch. An existing
   * batch among them is refilled, otherwise a new one takes over the first mesh's geometry; the replaced
   * objects leave the queue. Source meshes (in `sourceObjects`) keep their geometry for later instances,
   * and the batch's geometry owner is added to `sourceObjectsToRemove` for the caller to retire.
   */
  public createInstancedBatch(
    renderKeys: readonly string[],
    queue: HsSceneObjectQueue,
    graphics: HsGraphics,
    resolveMaterial: (renderKey: string) => HsMaterial,
    worldMatrixOf: (renderKey: string) => readonly number[] | undefined,
    sourceObjects: ReadonlySet<HsGraphicObject>,
    sourceObjectsToRemove: Set<HsGraphicObject>,
    referenceCounts: Map<HsGeometry, number>
  ): void {
    const objects: HsGraphicObject[] = renderKeys
      .map((renderKey: string) => this.meshes.get(renderKey))
      .filter((object: HsGraphicObject | undefined): object is HsGraphicObject => object !== undefined);
    if (
      objects.length !== renderKeys.length ||
      objects.some((object: HsGraphicObject) => !(object.geometry instanceof HsIndexedGeometry))
    ) {
      return;
    }
    this.invalidateTotals();
    this.hooks.invalidateTotals();
    this.hooks.refreshCulling();
    const existing: HsGraphicCollection | undefined = objects.find(
      (object: HsGraphicObject): object is HsGraphicCollection => object instanceof HsGraphicCollection
    );
    const collection: HsGraphicCollection =
      existing ??
      new HsGraphicCollection(
        getUuid(),
        `ingest-mesh-instanced-${renderKeys[0] ?? 'unknown'}`,
        objects[0].geometry,
        resolveMaterial(renderKeys[0] ?? '')
      );
    collection.isDeleted = false;
    collection.material = resolveMaterial(renderKeys[0] ?? '');
    if (existing !== undefined) {
      collection.clear(graphics);
    }
    for (const renderKey of renderKeys) {
      const worldMatrix: readonly number[] | undefined = worldMatrixOf(renderKey);
      const instanceMatrix: readonly number[] =
        Array.isArray(worldMatrix) && worldMatrix.length === 16
          ? worldMatrix
          : this.meshes.get(renderKey)?.worldMatrix.values ?? HsMatrix4.makeIdentityMatrix().values;
      collection.addInstance(instanceMatrix, resolveMaterial(renderKey).color.values);
    }
    collection.updateCenter();
    collection.updateBoundingSphere();
    collection.updateBuffers(graphics);
    this.renderKeysByCollection.set(collection, [...renderKeys]);
    queue.addObject(collection);
    if (existing === undefined) {
      this.retainGeometryReference(collection, referenceCounts);
    }

    const replaced: Set<HsGraphicObject> = new Set(objects);
    if (existing === undefined) {
      const geometryOwner: HsGraphicObject | undefined = objects[0];
      if (geometryOwner !== undefined) {
        replaced.delete(geometryOwner);
        sourceObjectsToRemove.add(geometryOwner);
      }
    }
    replaced.delete(collection);
    for (const object of replaced) {
      if (sourceObjects.has(object)) {
        queue.removeObject(object);
        object.isDeleted = true;
        this.releaseGeometryReference(object, referenceCounts);
      } else {
        this.dispose(queue, graphics, object, referenceCounts);
      }
    }
    for (const renderKey of renderKeys) {
      this.meshes.set(renderKey, collection);
    }
  }

  /**
   * Draws a decoded mesh under `renderKey`. An earlier object is kept when its geometry suits the material
   * (textured or not); otherwise another instance's geometry of the same source mesh is shared, or a new
   * geometry is built. Kept batches (`collection`) are left as they are.
   */
  public upsertMesh(
    renderKey: string,
    mesh: HsDecodedMesh,
    index: number,
    material: HsMaterial,
    options: HsSceneMeshUpsertOptions,
    queue: HsSceneObjectQueue & { objects: ReadonlyMap<string, unknown> },
    graphics: HsGraphics
  ): HsSceneMeshUpsertResult {
    const existing: HsGraphicObject | undefined = this.meshes.get(renderKey);
    if (existing !== undefined && !existing.isDeleted && !hsNeedsGeometryRebuild(existing, material)) {
      if (existing instanceof HsGraphicCollection) {
        if (!queue.objects.has(existing.uuid)) {
          queue.addObject(existing);
        }
        return { object: existing, kind: 'collection', rejection: null, indexedBuildFailed: false };
      }
      this.reuse(existing, material, options.worldMatrix, queue);
      return { object: existing, kind: 'reused', rejection: null, indexedBuildFailed: false };
    }
    if (options.shareSourceGeometry) {
      const source: HsGraphicObject | undefined = this.meshSources.get(options.sourceGeometryId);
      if (
        source !== undefined &&
        !source.isDeleted &&
        !source.geometry.isDeleted &&
        !hsNeedsGeometryRebuild(source, material)
      ) {
        const shared: HsGraphicObject = new HsGraphicObject(
          getUuid(),
          `ingest-mesh-shared-${index}`,
          source.geometry,
          material
        );
        this.place(shared, options.instanceWorldMatrix, queue);
        this.meshes.set(renderKey, shared);
        return { object: shared, kind: 'shared', rejection: null, indexedBuildFailed: false };
      }
    }
    const built: HsSceneMeshGeometryResult = hsBuildSceneMeshGeometry(graphics, mesh, {
      textured: material instanceof HsTextureMaterial,
      instancing: options.instancingGeometry,
    });
    if (built.geometry === null) {
      return { object: null, kind: null, rejection: built.rejection, indexedBuildFailed: built.indexedBuildFailed };
    }
    if (existing !== undefined) {
      this.dispose(queue, graphics, existing);
    }
    const object: HsGraphicObject = new HsGraphicObject(getUuid(), `ingest-mesh-${index}`, built.geometry, material);
    this.place(object, options.worldMatrix, queue);
    this.meshes.set(renderKey, object);
    if (options.shareSourceGeometry) {
      const source: HsGraphicObject | undefined = this.meshSources.get(options.sourceGeometryId);
      if (source === undefined || source.isDeleted || source.geometry.isDeleted) {
        this.meshSources.set(options.sourceGeometryId, object);
      }
    }
    return { object, kind: 'created', rejection: null, indexedBuildFailed: built.indexedBuildFailed };
  }

  /**
   * Draws a decoded line set under `renderKey`: an earlier object for the key is kept and updated, otherwise
   * a new one is created. Null when the line has no valid vertices or its geometry cannot be built.
   */
  public upsertLine(
    renderKey: string,
    line: HsDecodedLine,
    name: string,
    material: HsMaterial,
    worldMatrix: readonly number[] | undefined,
    queue: HsSceneObjectQueue & { objects: ReadonlyMap<string, unknown> },
    graphics: HsGraphics
  ): HsSceneUpsertResult<HsGraphicLines> | null {
    const existing: HsGraphicLines | undefined = this.lines.get(renderKey);
    if (existing !== undefined && !existing.isDeleted) {
      this.reuse(existing, material, worldMatrix, queue);
      return { object: existing, reused: true };
    }
    if (line.vertices.length === 0 || line.vertices.length % 3 !== 0) {
      return null;
    }
    if (existing !== undefined) {
      this.dispose(queue, graphics, existing);
    }
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      graphics,
      new HsRegularGeometryData(line.vertices, new Array<number>(line.vertices.length).fill(0))
    );
    if (geometry === null) {
      return null;
    }
    const object: HsGraphicLines = new HsGraphicLines(getUuid(), name, geometry, material);
    this.place(object, worldMatrix, queue);
    this.lines.set(renderKey, object);
    return { object, reused: false };
  }

  /**
   * Draws a decoded point cloud under `renderKey` like `upsertLine`. `collidedPoints` counts the points
   * that collide with the model, also for a kept object.
   */
  public upsertPointCloud(
    renderKey: string,
    pointCloud: HsDecodedPointCloud,
    name: string,
    material: HsPointsMaterial<HsDefaultShader>,
    worldMatrix: readonly number[] | undefined,
    options: { highlightCollisions: boolean; pointSize: number },
    queue: HsSceneObjectQueue & { objects: ReadonlyMap<string, unknown> },
    graphics: HsGraphics
  ): (HsSceneUpsertResult<HsGraphicPoints> & { collidedPoints: number }) | null {
    const existing: HsGraphicPoints | undefined = this.pointClouds.get(renderKey);
    if (existing !== undefined && !existing.isDeleted) {
      this.reuse(existing, material, worldMatrix, queue);
      let collidedPoints: number = 0;
      for (let i: number = 0; i < pointCloud.collisions.length; i++) {
        if ((pointCloud.collisions[i] ?? 0) > 0) {
          collidedPoints++;
        }
      }
      return { object: existing, reused: true, collidedPoints };
    }
    if (pointCloud.points.length === 0 || pointCloud.points.length % 3 !== 0) {
      return null;
    }
    if (existing !== undefined) {
      this.dispose(queue, graphics, existing);
    }
    const { colors, collidedPoints }: HsScenePointCloudColors = hsScenePointCloudColors(
      pointCloud,
      options.highlightCollisions
    );
    const geometry: HsRegularGeometry | null = hsMakePointCloudGeometry(graphics, pointCloud.points, colors);
    if (geometry === null) {
      return null;
    }
    const object: HsGraphicPoints = new HsGraphicPoints(getUuid(), name, geometry, material);
    if (Array.isArray(worldMatrix) && worldMatrix.length === 16) {
      object.worldMatrix.setFromArray(worldMatrix as HsHypersceneTypes.Array16);
    }
    object.setPointSize(options.pointSize);
    object.updateBoundingSphere();
    queue.addObject(object);
    this.hooks.invalidateOrder();
    this.pointClouds.set(renderKey, object);
    return { object, reused: false, collidedPoints };
  }

  /** Counts one more user of the object's geometry. */
  public retainGeometryReference(object: HsGraphicObject, referenceCounts: Map<HsGeometry, number>): void {
    referenceCounts.set(object.geometry, (referenceCounts.get(object.geometry) ?? 0) + 1);
  }

  /** Counts one user less of the object's geometry; returns the users left. */
  public releaseGeometryReference(object: HsGraphicObject, referenceCounts: Map<HsGeometry, number>): number {
    const next: number = Math.max(0, (referenceCounts.get(object.geometry) ?? 0) - 1);
    referenceCounts.set(object.geometry, next);
    return next;
  }

  private isGeometryUsedByAnotherObject(object: HsGraphicObject): boolean {
    // Runs once per removal during LOD swaps; scan the maps in place instead of collecting the objects.
    for (const map of [this.meshes, this.meshSources, this.lines, this.pointClouds] as Map<string, HsGraphicObject>[]) {
      for (const tracked of map.values()) {
        if (tracked !== object && !tracked.isDeleted && tracked.geometry === object.geometry) {
          return true;
        }
      }
    }
    return false;
  }

  private reuse(
    object: HsGraphicObject,
    material: HsMaterial,
    worldMatrix: readonly number[] | undefined,
    queue: HsSceneObjectQueue & { objects: ReadonlyMap<string, unknown> }
  ): void {
    if (!queue.objects.has(object.uuid)) {
      queue.addObject(object);
    }
    object.material = material;
    if (Array.isArray(worldMatrix) && worldMatrix.length === 16) {
      object.worldMatrix.setFromArray(worldMatrix as HsHypersceneTypes.Array16);
    }
    if (this.hooks.updateBounds !== undefined) {
      this.hooks.updateBounds(object);
    } else {
      object.updateBoundingSphere();
    }
  }

  private place(object: HsGraphicObject, worldMatrix: readonly number[] | undefined, queue: HsSceneObjectQueue): void {
    if (Array.isArray(worldMatrix) && worldMatrix.length === 16) {
      object.worldMatrix.setFromArray(worldMatrix as HsHypersceneTypes.Array16);
    }
    object.updateBoundingSphere();
    queue.addObject(object);
    this.hooks.invalidateOrder();
  }

  private trackedObjects(): Set<HsGraphicObject> {
    return new Set<HsGraphicObject>([
      ...this.meshes.values(),
      ...this.meshSources.values(),
      ...this.lines.values(),
      ...this.pointClouds.values(),
    ]);
  }
}

/** A textured material needs textured geometry (UVs) and the other way round; switching means rebuilding. */
export function hsNeedsGeometryRebuild(object: HsGraphicObject, material: HsMaterial): boolean {
  return object.geometry instanceof HsTexturedIndexedGeometry !== material instanceof HsTextureMaterial;
}

/** Triangles a geometry draws once: indexed by its indices, otherwise by its positions. */
export function hsCountGeometryTriangles(geometry: HsGeometry): number {
  return geometry instanceof HsIndexedGeometry
    ? Math.floor(geometry.indexArrayLength / 3)
    : Math.floor(geometry.positionArrayLength / 9);
}
